/**
 * Data Service - Offline-first data management
 * Automatically uses local storage when offline, API when online
 */

import axios from 'axios';
import StorageService from './storageService';

const API = process.env.REACT_APP_BACKEND_URL;

const DataService = {
  /**
   * Check if online
   */
  isOnline() {
    return navigator.onLine;
  },

  /**
   * Initialize data cache on login
   */
  async initializeCache(franchiseId) {
    try {
      console.log('[DataService] Initializing cache for franchise:', franchiseId);

      if (!this.isOnline()) {
        console.log('[DataService] Offline - skipping cache initialization');
        return false;
      }

      // Fetch and cache menu items
      const menuResponse = await axios.get(`${API}/api/menu-items?franchise_id=${franchiseId}`);
      const menuItems = menuResponse.data.menu_items || [];
      await StorageService.saveMenuItems(menuItems);
      console.log(`[DataService] Cached ${menuItems.length} menu items`);

      // Fetch and cache ledger parties
      try {
        const ledgerResponse = await axios.get(`${API}/api/ledger/parties?franchise_id=${franchiseId}`);
        const parties = ledgerResponse.data.parties || [];
        await StorageService.saveLedgerParties(parties, franchiseId);
        console.log(`[DataService] Cached ${parties.length} ledger parties`);
      } catch (error) {
        console.warn('[DataService] Could not cache ledger parties:', error);
      }

      // Set last sync time
      await StorageService.setLastSyncTime();

      console.log('[DataService] Cache initialization complete');
      return true;
    } catch (error) {
      console.error('[DataService] Cache initialization failed:', error);
      return false;
    }
  },

  /**
   * Get menu items (offline-first)
   */
  async getMenuItems(franchiseId) {
    try {
      // Try online first
      if (this.isOnline()) {
        const response = await axios.get(`${API}/api/menu-items?franchise_id=${franchiseId}`);
        const menuItems = response.data.menu_items || [];
        
        // Update cache in background
        StorageService.saveMenuItems(menuItems).catch(err => 
          console.warn('[DataService] Failed to update menu items cache:', err)
        );
        
        return menuItems;
      }

      // Fallback to local storage
      console.log('[DataService] Offline - using cached menu items');
      return await StorageService.getMenuItems();
    } catch (error) {
      console.warn('[DataService] Error fetching menu items, using cache:', error);
      return await StorageService.getMenuItems();
    }
  },

  /**
   * Get ledger parties (offline-first)
   */
  async getLedgerParties(franchiseId) {
    try {
      // Try online first
      if (this.isOnline()) {
        const response = await axios.get(`${API}/api/ledger/parties?franchise_id=${franchiseId}`);
        const parties = response.data.parties || [];
        
        // Update cache in background
        StorageService.saveLedgerParties(parties, franchiseId).catch(err =>
          console.warn('[DataService] Failed to update ledger parties cache:', err)
        );
        
        return parties;
      }

      // Fallback to local storage
      console.log('[DataService] Offline - using cached ledger parties');
      return await StorageService.getLedgerParties(franchiseId);
    } catch (error) {
      console.warn('[DataService] Error fetching ledger parties, using cache:', error);
      return await StorageService.getLedgerParties(franchiseId);
    }
  },

  /**
   * Create order (offline-first)
   */
  async createOrder(orderData, franchiseId) {
    try {
      // Generate order number
      const timestamp = Date.now();
      const orderNo = this.isOnline() 
        ? `${franchiseId}-${String(timestamp).slice(-6)}`
        : `${franchiseId}-OFFLINE-${timestamp}`;

      const order = {
        order_no: orderNo,
        franchise_id: franchiseId,
        items: orderData.items,
        total_value: orderData.total,
        ledger: orderData.ledger || 'cash',
        ledger_party_id: orderData.ledger_party_id || null,
        ledger_name: orderData.ledger_name || null,
        date_timestamp: new Date().toISOString(),
        order_details_json: JSON.stringify({
          items: orderData.items.map(item => ({
            menu_item_id: item.menu_item_id,
            name: item.item_name_snapshot,
            category: item.category || 'Uncategorized',
            price: item.item_price_snapshot,
            quantity: item.quantity,
            line_total: item.item_price_snapshot * item.quantity
          }))
        })
      };

      // Try to create online
      if (this.isOnline()) {
        try {
          const response = await axios.post(`${API}/api/orders`, {
            franchise_id: franchiseId,
            items: orderData.items,
            ledger: orderData.ledger || 'cash',
            ledger_party_id: orderData.ledger_party_id,
            ledger_name: orderData.ledger_name
          });

          if (response.data.success) {
            const serverOrderNo = response.data.order_no;
            order.order_no = serverOrderNo;
            
            // Save to synced orders history
            await StorageService.saveSyncedOrder(order);
            
            console.log('[DataService] Order created online:', serverOrderNo);
            return { success: true, order_no: serverOrderNo, offline: false };
          }
        } catch (apiError) {
          console.warn('[DataService] API failed, saving offline:', apiError);
          // Continue to offline save
        }
      }

      // Save offline
      await StorageService.savePendingOrder(order);
      console.log('[DataService] Order saved offline:', orderNo);
      
      return { 
        success: true, 
        order_no: orderNo, 
        offline: true,
        message: 'Order saved offline. Will sync when online.'
      };

    } catch (error) {
      console.error('[DataService] Error creating order:', error);
      throw error;
    }
  },

  /**
   * Get orders (offline-first)
   */
  async getOrders(franchiseId) {
    try {
      let orders = [];

      // Get synced orders from cache
      const syncedOrders = await StorageService.getSyncedOrders(franchiseId);
      orders = [...syncedOrders];

      // Get pending orders
      const pendingOrders = await StorageService.getPendingOrders(franchiseId);
      orders = [...orders, ...pendingOrders];

      // If online, fetch latest from server
      if (this.isOnline()) {
        try {
          const response = await axios.get(`${API}/api/orders?franchise_id=${franchiseId}`);
          const serverOrders = response.data.orders || [];
          
          // Merge with local orders (remove duplicates)
          const orderMap = new Map();
          [...serverOrders, ...orders].forEach(order => {
            orderMap.set(order.order_no, order);
          });
          
          orders = Array.from(orderMap.values());
        } catch (error) {
          console.warn('[DataService] Could not fetch server orders:', error);
        }
      }

      // Sort by date
      orders.sort((a, b) => new Date(b.date_timestamp) - new Date(a.date_timestamp));
      
      return orders;
    } catch (error) {
      console.error('[DataService] Error getting orders:', error);
      return [];
    }
  },

  /**
   * Get sync stats
   */
  async getSyncStats() {
    return await StorageService.getSyncStats();
  }
};

export default DataService;
